import PropTypes from 'prop-types';
import React from 'react';
import { Form, TooltipHelp, SelectLabel, SelectHierarchy, Tooltip } from '@weezevent/nacre';
import { Icon } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import OrgaOnly from '../../components/orgaOnly';
import { RateTemplateSeatingForm } from './seating/rate-seating-form';
import { InheritedInput } from './rateForm';
import EventOnly from '../../components/eventOnly';
import { formatDateForInventories } from '../../utils/date';
import Styles from '../../styles/rate/rateCreation.module.css';

const ToggleInheritedContingent = ({ form, onChangeContingents, rate }) => {
    const { t } = useTranslation();
    return (
        <div className="inherited-label-value-container">
            <Tooltip
                triggerOnHover
                title={<span>{form.contingents?.length ? t('ticket.rate_template.link_field_template') : t('ticket.rate_template.unlink_field_template')}</span>}
            >
                <Icon
                    className="inherit-label-icon"
                    name={form.contingents?.length ? 'unlink' : 'linkify'}
                    onClick={() => {
                        if (form.contingents?.length) {
                            onChangeContingents([]);
                        } else {
                            onChangeContingents(rate.rate_template.contingents);
                        }
                    }}
                />
            </Tooltip>
        </div>
    );
};

export default function RateFormSeating({
    organizationId,
    event = null,
    form,
    onChangeForm,
    onChangeFormObject,
    formErrors,
    rateTemplates = [],
    categories,
    inventories,
    contingents,
    charts,
    rate
}) {
    const { t } = useTranslation();
    const locale = i18n.language;

    const [selected, setSelected] = React.useState(rate?.contingents ? rate.contingents.map(contingent => contingent) : []);
    const mapToOptions = React.useCallback(
        object => ({
            key: object.pk,
            text: (event?.isFakeSeatingMultipleInventories() && object.start_date ? formatDateForInventories(object.start_date, locale, event.timezone) : null) || object.name,
            value: object.pk,
            object: object
        }),
        [event]
    );
    const [categorySearch, setCategorySearch] = React.useState('');
    const filteredCategoriesOptions = React.useMemo(() => categories.map(mapToOptions).filter(o => o.text.toLowerCase().includes(categorySearch.toLowerCase())), [
        categories,
        mapToOptions,
        categorySearch
    ]);

    const optionsContingents = React.useMemo(() => contingents.map(mapToOptions), [contingents, mapToOptions]);
    const optionsInventories = React.useMemo(() => {
        let _options = [];

        // Ordering alphabetically if same date and is not fakeseating multiple inventories
        if (!event?.isFakeSeatingMultipleInventories?.()) {
            _options = inventories.map(mapToOptions).sort((i1, i2) => {
                if (i1.text < i2.text) return -1;
                if (i1.text > i2.text) return 1;
                return 0;
            });
        } else {
            // Chronological sort for inventories fake seated and multiples dates
            _options = inventories
                .slice()
                .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
                .map(mapToOptions);
        }

        _options.unshift({ value: null, text: 'None' });
        return _options;
    }, [inventories, mapToOptions]);

    const isGA = React.useMemo(() => {
        return form.category && categories.find(cat => cat.pk === form.category)?.general_admission;
    }, [form.category, categories]);

    const onChangeCategory = React.useCallback(
        value => {
            if (value && categories.find(cat => cat.pk === value)?.general_admission) {
                onChangeForm('contingents', null);
            }
            onChangeForm('category', value);
        },
        [onChangeForm]
    );

    const onChangeContingents = React.useCallback(
        selected => {
            let selectedTmp = !Array.isArray(selected) ? [selected] : selected;
            setSelected(selectedTmp);
            onChangeForm('contingents', selectedTmp);
        },
        [setSelected, onChangeForm]
    );
    const handleLabelChange = React.useCallback(
        currentSelected => {
            let count = rate.rate_template && !form.contingents?.length ? rate.rate_template.contingents?.length : currentSelected.length;
            return t('ticket.rates.contingentsAll', { count: count || 0 });
        },
        [rate, form]
    );

    return (
        <>
            <EventOnly event={event}>
                {!event?.use_sessions && (
                    <Form.Field>
                        <div className={Styles['required-tooltip-flex']}>
                            <Form.Label
                                label={t(event?.isFakeSeatingMultipleInventories() ? 'ticket.rates.passculture.fakeSeating.inventoryTitle' : 'ticket.rates.inventories')}
                                required
                            />
                            <TooltipHelp title={t('ticket.rates.inventoryInformation')} mini />
                        </div>
                        <SelectLabel
                            errors={Boolean(formErrors['inventories'])}
                            options={optionsInventories}
                            onChange={(synth, data) => onChangeForm('inventories', data.value)}
                            value={form.inventories || null}
                            placeholder={t('ticket.rates.inventories-placeholder')}
                        />
                        <Form.FieldErrors errors={formErrors['inventories'] ? [t(formErrors['inventories'])] : null} />
                    </Form.Field>
                )}

                {(event?.is_seated || (event?.isFakeSeating() && categories.length > 1)) && (
                    <>
                        <Form.Field className="inherited-custom-input">
                            <div className={Styles['required-tooltip-flex']}>
                                <Form.Label label={t('ticket.rates.category')} required />
                                <TooltipHelp title={t('ticket.rates.categoryInformation')} mini />
                            </div>
                            <InheritedInput onChange={value => onChangeCategory(value)} formState={form} property="category" rate={rate}>
                                <SelectLabel
                                    errors={Boolean(formErrors['category'])}
                                    options={filteredCategoriesOptions}
                                    onChange={(synth, data) => {
                                        onChangeCategory(data.value);
                                        setCategorySearch('');
                                    }}
                                    placeholder={t('ticket.rates.category-placeholder')}
                                    value={form.category}
                                    search
                                    useNew
                                    clearable
                                    onClear={() => onChangeCategory(null)}
                                    onInputChange={e => setCategorySearch(e.target.value)}
                                />
                            </InheritedInput>
                            <Form.FieldErrors errors={formErrors['category'] ? [t(formErrors['category'])] : null} />
                        </Form.Field>
                        {!isGA || (form.contingents && form.contingents.length) ? (
                            <Form.Field className="inherited-custom-input">
                                <Form.Label>
                                    {t('ticket.rates.contingents')}
                                    <TooltipHelp title={t('ticket.rates.contingentsInformation')} />
                                </Form.Label>
                                <div className="inherited-contingents-input">
                                    <SelectHierarchy
                                        key={form.contingents?.length}
                                        multi
                                        disabled={isGA || (rate.rate_template && !form.contingents?.length)}
                                        selected={selected}
                                        labelSelectAll={t('ticket.rates.contingentsSelectAll')}
                                        options={optionsContingents}
                                        totalItems={optionsContingents.length}
                                        onChange={onChangeContingents}
                                        onLabelSelected={handleLabelChange}
                                        placeholder={t('ticket.rates.contingents-placeholder')}
                                    />
                                    {rate.rate_template && <ToggleInheritedContingent form={form} rate={rate} onChangeContingents={onChangeContingents} />}
                                </div>
                            </Form.Field>
                        ) : null}
                    </>
                )}
            </EventOnly>
            <OrgaOnly event={event}>
                <RateTemplateSeatingForm rateTemplate={rate} charts={charts} form={form} categories={categories} contingents={contingents} updateForm={onChangeForm} />
            </OrgaOnly>
        </>
    );
}

RateFormSeating.propTypes = {
    organizationId: PropTypes.number.isRequired,
    event: PropTypes.object,
    form: PropTypes.object.isRequired,
    rate: PropTypes.object.isRequired,
    rateTemplates: PropTypes.array,
    onChangeForm: PropTypes.func.isRequired,
    onChangeFormObject: PropTypes.func.isRequired,
    formErrors: PropTypes.object.isRequired,
    categories: PropTypes.array.isRequired,
    inventories: PropTypes.array.isRequired,
    contingents: PropTypes.array.isRequired,
    charts: PropTypes.array
};
